/**
 * ScheduleManageModal - 수업 상세/관리 모달 (instructor-web)
 *
 * instructor-web에는 수업 상세 진입점이 없어 신설한다. admin-web의 ScheduleDetailModal
 * (예약취소)에 수업 변경/삭제 진입을 더한 통합 관리 모달이다.
 * - 예약취소: 강사 가능(cancelInstructorBooking)
 * - 수업 변경: 강사 가능 — onEdit으로 부모가 EditScheduleModal을 연다
 * - 수업 삭제: 강사 가능(deleteInstructorSchedule)
 */

import { useState } from "react";
import { Card, Button, Alert } from "@vlab-corporation/design-system";
import type { StudiomateScheduleItem } from "@vlab-corporation/studiomate-api";
import { useCancelBooking, useDeleteSchedule } from "@/hooks/useCreateSchedule";

interface ScheduleManageModalProps {
  isOpen: boolean;
  centerId: number;
  schedule: StudiomateScheduleItem;
  onClose: () => void;
  /** "수업 변경" 클릭 — 부모가 EditScheduleModal을 연다 */
  onEdit: () => void;
  /** 예약취소/삭제 성공 시 — 부모가 목록을 refetch */
  onChanged: () => void;
}

export function ScheduleManageModal({
  isOpen,
  centerId,
  schedule,
  onClose,
  onEdit,
  onChanged,
}: ScheduleManageModalProps) {
  const cancelBooking = useCancelBooking();
  const deleteSchedule = useDeleteSchedule();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleCancelBooking = (bookingId: number) => {
    setActionError(null);
    cancelBooking.mutate(
      { centerId, scheduleId: schedule.id, bookingId },
      {
        onSuccess: () => onChanged(),
        onError: () => setActionError("예약 취소에 실패했습니다."),
      },
    );
  };

  const handleDeleteClick = () => {
    setConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    setActionError(null);
    deleteSchedule.mutate(
      { centerId, scheduleId: schedule.id },
      {
        onSuccess: () => {
          onChanged();
          onClose();
        },
        onError: () => setActionError("수업 삭제에 실패했습니다."),
      },
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
      <Card className="w-full max-w-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">수업 관리</h2>
        <p className="text-sm text-gray-500 mb-4">
          {schedule.date} {schedule.startTime}~{schedule.endTime} · {schedule.title} ·{" "}
          {schedule.staffName} ({schedule.currentTrainees}/{schedule.maxTrainees})
        </p>

        {actionError && (
          <Alert variant="error" className="mb-3">
            {actionError}
          </Alert>
        )}

        <h3 className="text-sm font-semibold text-gray-700 mb-2">예약 회원</h3>
        {schedule.bookings.length === 0 ? (
          <p className="text-sm text-gray-500 mb-4">예약된 회원이 없습니다.</p>
        ) : (
          <div className="space-y-1.5 mb-4">
            {schedule.bookings.map((b, idx) => (
              <div
                key={b.bookingId ?? `${b.memberName}-${idx}`}
                className="flex items-center justify-between text-sm border border-gray-100 rounded-lg px-3 py-2"
              >
                <span className="min-w-0 truncate">
                  {b.memberName}
                  {b.ticketTitle && (
                    <span className="text-gray-400 text-xs ml-2">{b.ticketTitle}</span>
                  )}
                </span>
                {b.bookingId != null ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCancelBooking(b.bookingId as number)}
                    disabled={cancelBooking.isPending}
                  >
                    예약취소
                  </Button>
                ) : (
                  <span className="text-xs text-gray-300">-</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 mt-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDeleteClick}
            disabled={deleteSchedule.isPending}
          >
            수업 삭제
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              닫기
            </Button>
            <Button variant="primary" size="sm" onClick={onEdit}>
              수업 변경
            </Button>
          </div>
        </div>

        {/* 삭제 확인 */}
        {confirmDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] px-4">
            <Card className="w-full max-w-sm p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-2">수업 삭제</h3>
              <p className="text-sm text-gray-600 mb-5">
                "{schedule.title}" 수업을 삭제하시겠습니까? 예약 회원이 있으면 함께 정리됩니다.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleteSchedule.isPending}
                >
                  취소
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmDelete}
                  disabled={deleteSchedule.isPending}
                >
                  {deleteSchedule.isPending ? "삭제 중..." : "삭제"}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </Card>
    </div>
  );
}
